/**
 * Pantallas del reproductor de la batería PEFF. Toda la lógica de transición
 * vive en la máquina de estados pura (peff-player.ts) y los efectos en
 * usePeffPlayer.ts: aquí solo se pinta el estado y se despachan intenciones.
 *
 * Reglas clínicas visibles en esta capa (master §2.1/§3.1):
 *  - F2/PSF1 (denominación): imagen del corpus; sin imagen, fallback a texto.
 *  - F4 jamás muestra la oración al niño; el adulto la revela con 👁️ (blur).
 *  - Nunca se dice "bien/mal": refuerzo neutro y estrellas por completitud.
 */
import { useState } from "react";
import { Mascot, SpeechBubble } from "../components/Mascot.js";
import { Button } from "../components/Button.js";
import { Confetti } from "../components/Confetti.js";
import { useT } from "../i18n/index.js";
import type { BandaEtaria, BateriaCompletaDTO } from "../api/types.js";
import { MicCheck } from "./MicCheck.js";
import { reproducir } from "./audio.js";
import { claveConTono, tonoDeBanda } from "./tono.js";
import {
  ejercicioActual,
  itemActual,
  progreso,
  puedeAvanzar,
  type EstadoPlayer,
} from "./peff-player.js";
import { usePeffPlayer } from "./usePeffPlayer.js";
import type { InitPlayerInput } from "./peff-player.js";
import type { MessageKey } from "../i18n/index.js";
import styles from "./EvaluationPlayer.module.css";

/** Botón "escuchar otra vez": repite el audio modelo a pedido del adulto. Sin
 *  audio activo NO se muestra (no hay TTS de relleno). */
function ListenButton({ audioUrl }: { audioUrl: string | null }) {
  const t = useT();
  const [playing, setPlaying] = useState(false);
  if (!audioUrl) return null;
  return (
    <button
      className={[styles.playBtn, playing ? styles.playing : ""].join(" ")}
      onClick={() => {
        setPlaying(true);
        void reproducir({ audioRef: audioUrl }).finally(() => setPlaying(false));
      }}
    >
      {t("eval.listen")}
    </button>
  );
}

/** F4: la oración solo la ve el adulto, con blur y revelado explícito. */
function EvaluatorPeek({ texto }: { texto: string }) {
  const t = useT();
  const [reveal, setReveal] = useState(false);
  return (
    <div className={[styles.peek, reveal ? styles.reveal : ""].join(" ")}>
      <b>{t("eval.peff.adult.peek")}:</b>
      <span className={styles.peekText}>“{texto}”</span>
      <button className={styles.eye} onClick={() => setReveal((v) => !v)} aria-label="Mostrar u ocultar">
        👁️
      </button>
    </div>
  );
}

/**
 * Imagen de estímulo del corpus (word_media). Fallback VISUAL imagen → texto
 * (sin emoji intermedio): si no hay imagen activa, o la URL falla/expira en
 * runtime, se muestra el texto del ítem para no romper la sesión.
 */
function EstimuloVisual({ imagenUrl, texto }: { imagenUrl: string | null; texto: string }) {
  const [falló, setFalló] = useState(false);
  const mostrarImagen = !!imagenUrl && !falló;
  return (
    <div className={styles.img}>
      {mostrarImagen ? (
        <img
          src={imagenUrl!}
          alt=""
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          onError={() => setFalló(true)}
        />
      ) : (
        <span className={styles.stimText}>{texto}</span>
      )}
    </div>
  );
}

type Player = ReturnType<typeof usePeffPlayer>;

function Estimulo({ s, player }: { s: EstadoPlayer; player: Player }) {
  const t = useT();
  const item = itemActual(s);
  const ejercicio = ejercicioActual(s);
  if (!item || !ejercicio) return null;
  const m = player.mecanica;

  if (m.grafema) {
    // F1: grafema en grande + audio modelo automático (ya sonó al entrar).
    return (
      <div className={styles.stimCard}>
        <div className={styles.phoneme}>{item.ref}</div>
        <ListenButton audioUrl={item.audioModeloUrl} />
      </div>
    );
  }

  if (m.soloAudio) {
    // F4: solo audio + animación de escucha. El texto NUNCA se muestra al niño.
    return (
      <>
        <div className={styles.audioOnly}>
          <span className={styles.rings}>
            <i />
            <i />
            <i />
          </span>
          <span className={styles.ear}>👂</span>
        </div>
        <ListenButton audioUrl={item.audioModeloUrl} />
        {m.peekAdulto ? <EvaluatorPeek texto={item.ref} /> : null}
      </>
    );
  }

  if (m.distractora) {
    // PSF2: modelo → pregunta distractora (no se graba) → "Ahora dila tú".
    return (
      <div className={styles.stimCard}>
        <div className={styles.audioOnly}>
          <span className={styles.ear}>{s.paso === "modelo" ? "👂" : "🗣️"}</span>
        </div>
        {s.paso === "distractora" ? (
          <div className={styles.distractor}>
            <SpeechBubble>{player.distractora}</SpeechBubble>
            <Button variant="primary" onClick={player.distractoraOk}>
              {t("eval.peff.item.distractoraOk")}
            </Button>
          </div>
        ) : null}
        {s.paso === "consigna" || s.paso === "respuesta" ? (
          <SpeechBubble>{t("eval.peff.item.ahoraTu")}</SpeechBubble>
        ) : null}
      </div>
    );
  }

  // F2 / PSF1 (denominación) y F3 (repetición con imagen).
  return (
    <>
      {m.denominacion ? <SpeechBubble>{t("eval.peff.item.queEs")} 👀</SpeechBubble> : null}
      <div className={styles.stimCard}>
        <EstimuloVisual imagenUrl={item.imagenUrl} texto={item.ref} />
        {m.modeloAuto ? <ListenButton audioUrl={item.audioModeloUrl} /> : null}
      </div>
      {s.paso === "pista" ? (
        <div className={styles.hint}>
          <SpeechBubble>{item.pistaSemantica ?? t("eval.peff.item.pistaPlaceholder")}</SpeechBubble>
        </div>
      ) : null}
      {s.paso === "no_evoca" ? <SpeechBubble>{t("eval.peff.item.noEvoca")}</SpeechBubble> : null}
    </>
  );
}

export interface PeffPlayerProps {
  bateria: BateriaCompletaDTO;
  plan: InitPlayerInput;
  banda: BandaEtaria;
  onExit: () => void;
  onDone: (bateriaId: string) => void;
}

export function PeffPlayer(props: PeffPlayerProps) {
  const t = useT();
  const tono = tonoDeBanda(props.banda);
  /** t() con el registro de la banda (BIG_KID en banda D). */
  const tt = (key: MessageKey, params?: Record<string, string | number>) =>
    t(claveConTono(key, tono), params);

  const player = usePeffPlayer(props.bateria, props.plan, {
    pistaPlaceholder: t("eval.peff.item.pistaPlaceholder"),
    distractoraPlaceholder: t("eval.peff.item.distractoraPlaceholder"),
    ahoraTu: t("eval.peff.item.ahoraTu"),
  });
  const { state } = player;
  const ejercicio = ejercicioActual(state);

  // ---- prueba de micrófono (obligatoria) ----
  if (state.estado === "mic_check") return <MicCheck onOk={() => void player.micOk()} />;

  // ---- consigna del ejercicio (literal del master) ----
  if (state.estado === "consigna_ejercicio" && ejercicio) {
    return (
      <section className={styles.kid}>
        <div className={styles.body}>
          <SpeechBubble>{tt(`eval.peff.consigna.${ejercicio.codigo}` as MessageKey)}</SpeechBubble>
          <Mascot size={tono === "BIG_KID" ? 90 : 130} />
          <Button
            variant="sun"
            disabled={player.iniciando}
            onClick={() => void player.iniciarEjercicio()}
          >
            {state.error ? t("common.retry") : tt("eval.peff.consigna.start")}
          </Button>
          {state.error ? (
            <span className={styles.recLabel} style={{ color: "var(--danger)" }}>
              {state.error}
            </span>
          ) : null}
        </div>
      </section>
    );
  }

  // ---- pausa entre ejercicios ----
  if (state.estado === "pausa") {
    return (
      <section className={styles.kid}>
        <div className={styles.party}>
          <div className={styles.medal}>🌿</div>
          <div className={styles.bigTitle}>{t("eval.peff.pausa.title")}</div>
          <p style={{ color: "var(--ink-soft)", maxWidth: 320 }}>{t("eval.peff.pausa.body")}</p>
          <div className={styles.stars}>{t("eval.peff.stars", { n: state.estrellas })}</div>
          <Button variant="sun" onClick={player.siguienteEjercicio}>
            {t("eval.peff.pausa.continue")}
          </Button>
        </div>
      </section>
    );
  }

  // ---- suspensión (adulto o negativa sostenida) ----
  if (state.estado === "suspendida") {
    return (
      <section className={styles.kid}>
        <div className={styles.party}>
          <div className={styles.medal}>💾</div>
          <div className={styles.bigTitle}>{t("eval.peff.suspend.title")}</div>
          <p style={{ color: "var(--ink-soft)", maxWidth: 320 }}>{t("eval.peff.suspend.body")}</p>
          <Button variant="sun" onClick={() => void player.reanudar()}>
            {t("eval.peff.suspend.resume")}
          </Button>
          <Button variant="ghost" onClick={props.onExit}>
            {t("eval.peff.suspend.exit")}
          </Button>
        </div>
      </section>
    );
  }

  // ---- cierre de la sesión del día (banda A: 2 sesiones) ----
  if (state.estado === "sesion_cerrada") {
    return (
      <section className={styles.kid}>
        <Confetti />
        <div className={styles.party}>
          <div className={styles.medal}>🏅</div>
          <div className={styles.bigTitle}>{t("eval.peff.sesion.title")}</div>
          <p style={{ color: "var(--ink-soft)", maxWidth: 320 }}>{t("eval.peff.sesion.body")}</p>
          <div className={styles.stars}>{t("eval.peff.stars", { n: state.estrellas })}</div>
          <Mascot size={90} />
          <Button variant="sun" onClick={props.onExit}>
            {t("eval.peff.sesion.exit")}
          </Button>
        </div>
      </section>
    );
  }

  // ---- procesando el pre-informe ----
  if (state.estado === "listo_para_procesar" || state.estado === "procesando") {
    return (
      <section className={styles.kid}>
        <div className={styles.party}>
          <div className={styles.audioOnly}>
            <span className={styles.rings}>
              <i />
              <i />
              <i />
            </span>
            <span className={styles.ear}>🧠</span>
          </div>
          <h2>{t("eval.peff.close.processing")}</h2>
          <p style={{ color: "var(--ink-soft)", maxWidth: 300 }}>
            {t("eval.peff.close.processingHint")}
          </p>
        </div>
      </section>
    );
  }

  // ---- batería cerrada: aviso de pre-informe ----
  if (state.estado === "hecho") {
    return (
      <section className={styles.kid}>
        <Confetti />
        <div className={styles.party}>
          <div className={styles.medal}>🏅</div>
          <div className={styles.bigTitle}>{t("eval.done.title")}</div>
          <div className={styles.stars}>{t("eval.peff.stars", { n: state.estrellas })}</div>
          <p style={{ color: "var(--ink-soft)", maxWidth: 320 }}>
            <b>{t("eval.peff.close.ready")}</b>
            <br />
            {t("eval.peff.close.readyHint")}
          </p>
          <Mascot size={90} />
          <Button variant="sun" onClick={() => props.onDone(props.bateria.id)}>
            {t("eval.peff.close.toReport")} 📋
          </Button>
        </div>
      </section>
    );
  }

  // ---- administración del ítem ----
  const { n, total, pct } = progreso(state);
  const grabando = state.rec === "grabando";
  const guardado = state.rec === "guardado";
  const subiendo = state.rec === "subiendo";
  const puedeGrabar = state.paso === "respuesta" || state.paso === "pregunta";

  const recLabel =
    state.rec === "error"
      ? (state.error ?? t("common.error"))
      : grabando
        ? t("eval.rec.recording")
        : subiendo
          ? t("common.saving")
          : guardado
            ? t("eval.rec.saved")
            : player.mecanica.denominacion
              ? t("eval.rec.tapName")
              : t("eval.rec.tapRepeat");

  return (
    <section className={styles.kid}>
      <div className={styles.kidTop}>
        <button className={styles.exit} onClick={() => void player.suspender()} aria-label={t("eval.peff.suspend.button")}>
          ✕
        </button>
        <span className={styles.pill}>
          {ejercicio ? t(`eval.peff.ej.${ejercicio.codigo}` as MessageKey) : ""} · {n}/{total}
        </span>
        <span className={styles.stars}>{t("eval.peff.stars", { n: state.estrellas })}</span>
      </div>
      <div className={styles.track}>
        <div className={styles.fill} style={{ width: `${Math.max(4, pct)}%` }} />
      </div>

      <div className={styles.body}>
        <Estimulo s={state} player={player} />
      </div>

      <div className={styles.recZone}>
        <div className={[styles.wave, grabando ? styles.on : ""].join(" ")} aria-hidden>
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
        <button
          className={[styles.recBtn, grabando ? styles.recording : "", guardado ? styles.done : ""].join(" ")}
          disabled={subiendo || !puedeGrabar}
          aria-label={grabando ? "Detener grabación" : "Grabar respuesta"}
          onClick={() => {
            if (grabando) void player.grabarFin();
            else if (guardado) player.regrabar();
            else if (state.rec === "error") void player.reintentarSubida();
            else void player.grabarInicio(player.mecanica.autoStop);
          }}
        >
          {guardado ? "✓" : grabando ? "🔴" : "🎙️"}
        </button>
        <span className={styles.recLabel}>{recLabel}</span>

        {/* Controles del adulto: nunca califican al niño, solo describen lo que
            pasó (no lo intentó / producción dudosa → el API arma el adaptativo). */}
        <div className={styles.adultBar}>
          <button className={styles.ghostBtn} onClick={player.marcarSinIntento}>
            {t("eval.peff.adult.noAttempt")}
          </button>
          <button
            className={styles.ghostBtn}
            onClick={player.marcarDudoso}
            disabled={state.flags.dudoso}
          >
            {t("eval.peff.adult.doubt")}
          </button>
          <button
            className={styles.nextBtn}
            onClick={() => void player.avanzar()}
            disabled={!puedeAvanzar(state)}
            aria-label={t("eval.peff.adult.next")}
          >
            ➜
          </button>
        </div>
      </div>
    </section>
  );
}
